import type { ActivityItem } from '../hooks/useActivityCenter';
import { nowISO } from '@/utils/dateUtils';
import { isPrescriber } from '@/utils/prescriberHelpers';

export interface DoseSpotAccessStatus {
  enrolled?: boolean;
}

export function shouldPollDoseSpotNotifications(
  user: { can_prescribe?: boolean } | false | null | undefined,
  access: DoseSpotAccessStatus | null | undefined,
): boolean {
  return isPrescriber(user) && access?.enrolled === true;
}

export interface DoseSpotNotificationCounts {
  pending_prescriptions?: number;
  transmission_errors?: number;
  refill_requests?: number;
  rxchange_requests?: number;
}

export const DOSESPOT_RXCHANGE_ITEM_ID = 'dosespot-rxchange';
export const DOSESPOT_RXCHANGE_TITLE = 'DoseSpot RxChange needs review';
export const DOSESPOT_ENTRYPOINT_ITEM_ID = 'dosespot-entrypoint';
export const DOSESPOT_ENTRYPOINT_TITLE = 'Open DoseSpot notifications';

export const DOSESPOT_NOTIFICATION_DEFINITIONS = [
  {
    countKey: 'pending_prescriptions',
    id: 'dosespot-pending-prescriptions',
    title: 'DoseSpot prescriptions need review',
    message: (count: number) =>
      `${count} pending prescription notification${count === 1 ? '' : 's'}.`,
  },
  {
    countKey: 'transmission_errors',
    id: 'dosespot-transmission-errors',
    title: 'DoseSpot transmission error needs review',
    message: (count: number) =>
      `${count} prescription transmission error${count === 1 ? '' : 's'} pending.`,
  },
  {
    countKey: 'refill_requests',
    id: 'dosespot-refill-requests',
    title: 'DoseSpot refill request needs review',
    message: (count: number) =>
      `${count} refill request notification${count === 1 ? '' : 's'} pending.`,
  },
  {
    countKey: 'rxchange_requests',
    id: DOSESPOT_RXCHANGE_ITEM_ID,
    title: DOSESPOT_RXCHANGE_TITLE,
    message: (count: number) =>
      `${count} medication change notification${count === 1 ? '' : 's'} pending.`,
  },
] as const;

export function isDoseSpotNotificationItem(itemId: string): boolean {
  return (
    itemId === DOSESPOT_ENTRYPOINT_ITEM_ID ||
    DOSESPOT_NOTIFICATION_DEFINITIONS.some((definition) => definition.id === itemId)
  );
}

function getAdditionalDoseSpotCount(
  pendingCount: number,
  title: string,
  items: ActivityItem[],
): number {
  const persistedUnreadCount = items.filter(
    (item) =>
      item.source === 'system' &&
      item.title === title &&
      item.read_state === 'unread',
  ).length;
  return Math.max(0, pendingCount - persistedUnreadCount);
}

export function getAdditionalDoseSpotRxChangeCount(
  pendingCount: number,
  items: ActivityItem[],
): number {
  return getAdditionalDoseSpotCount(pendingCount, DOSESPOT_RXCHANGE_TITLE, items);
}

export function getDoseSpotNotificationItems(
  counts: DoseSpotNotificationCounts,
  items: ActivityItem[],
): ActivityItem[] {
  const timestamp = nowISO();
  const entrypointItem = {
    id: DOSESPOT_ENTRYPOINT_ITEM_ID,
    source: 'system',
    source_id: DOSESPOT_ENTRYPOINT_ITEM_ID,
    item_type: 'informational',
    status: 'active',
    read_state: 'read',
    priority: 'low',
    title: DOSESPOT_ENTRYPOINT_TITLE,
    message: 'Open the DoseSpot notification summary.',
    deep_link: null,
    link_params: null,
    due_at: null,
    snooze_until: null,
    created_at: timestamp,
    updated_at: timestamp,
  } as ActivityItem;
  const pendingItems = DOSESPOT_NOTIFICATION_DEFINITIONS.flatMap((definition) => {
    const count = counts[definition.countKey] || 0;
    const additionalCount = getAdditionalDoseSpotCount(count, definition.title, items);
    if (additionalCount === 0) return [];
    return [
      {
        id: definition.id,
        source: 'system',
        source_id: definition.id,
        item_type: 'action_required',
        status: 'active',
        read_state: 'unread',
        priority: definition.countKey === 'transmission_errors' ? 'urgent' : 'high',
        title: definition.title,
        message: definition.message(additionalCount),
        deep_link: null,
        link_params: null,
        due_at: null,
        snooze_until: null,
        created_at: timestamp,
        updated_at: timestamp,
      } as ActivityItem,
    ];
  });
  return [entrypointItem, ...pendingItems];
}

export function getAdditionalDoseSpotNotificationCount(
  counts: DoseSpotNotificationCounts,
  items: ActivityItem[],
): number {
  return DOSESPOT_NOTIFICATION_DEFINITIONS.reduce(
    (total, definition) =>
      total +
      getAdditionalDoseSpotCount(counts[definition.countKey] || 0, definition.title, items),
    0,
  );
}
